import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { motion, AnimatePresence } from 'framer-motion';
import { Mic, ArrowRight, Loader2, Sparkles, Lock, ChevronDown } from 'lucide-react';
import { TarotCardFront, FlippableCard } from '../components/TarotCard';
import { ShareReading } from '../components/ShareReading';
import api from '../lib/api';
import Navigation from '../components/Navigation';
import { INTENTIONS } from '../data/tarotDeck';

const SHUFFLE_MESSAGES = [
  "Take a moment to reflect on your question...",
  "Close your eyes. Breathe in. Breathe out.",
  "The cards are listening to your energy...",
  "Let your mind settle on what matters most.",
  "The deck is aligning with your intention...",
  "Something is coming through for you...",
];

function ShuffleMessages() {
  const [msgIndex, setMsgIndex] = useState(0);
  useEffect(() => {
    const interval = setInterval(() => {
      setMsgIndex(prev => (prev + 1) % SHUFFLE_MESSAGES.length);
    }, 4000);
    return () => clearInterval(interval);
  }, []);
  return (
    <div className="text-center max-w-md" data-testid="shuffle-message">
      <AnimatePresence mode="wait">
        <motion.p
          key={msgIndex}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
          transition={{ duration: 0.6 }}
          className="font-['Playfair_Display'] text-lg text-[#FFFFF0] italic"
        >
          {SHUFFLE_MESSAGES[msgIndex]}
        </motion.p>
      </AnimatePresence>
      <motion.div
        className="flex justify-center gap-1.5 mt-5"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.5 }}
      >
        {[0, 1, 2].map(i => (
          <motion.div
            key={i}
            className="w-1.5 h-1.5 rounded-full bg-[#D4AF37]"
            animate={{ opacity: [0.3, 1, 0.3] }}
            transition={{ duration: 1.2, repeat: Infinity, delay: i * 0.3 }}
          />
        ))}
      </motion.div>
    </div>
  );
}

export default function HomePage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [phase, setPhase] = useState('question');
  const [intentions, setIntentions] = useState([]);
  const [question, setQuestion] = useState('');
  const [spreadType, setSpreadType] = useState('');
  const [spreads, setSpreads] = useState([]);
  const [cards, setCards] = useState([]);
  const [flippedCards, setFlippedCards] = useState([]);
  const [readingData, setReadingData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [surpriseMsg, setSurpriseMsg] = useState('');
  const [isListening, setIsListening] = useState(false);
  const [levelUp, setLevelUp] = useState(null);
  const [summarizing, setSummarizing] = useState(false);
  const readingRef = useRef(null);

  useEffect(() => {
    api.get('/profile/stats').then(({ data }) => {
      if (data.spreads) setSpreads(data.spreads);
    }).catch(() => {});
  }, []);

  // Auto-populate question from chat session
  useEffect(() => {
    const chatSessionId = location.state?.chatSessionId;
    if (chatSessionId) {
      setSummarizing(true);
      api.post('/chat/summarize-for-reading', { session_id: chatSessionId })
        .then(({ data }) => {
          if (data.question) setQuestion(data.question);
        })
        .catch(() => {})
        .finally(() => setSummarizing(false));
      // Clear the state so it doesn't re-trigger
      window.history.replaceState({}, '');
    }
  }, [location.state]);

  const canDraw = question.trim().length > 0;

  const startListening = () => {
    if (!('webkitSpeechRecognition' in window) && !('SpeechRecognition' in window)) { setError('Voice input not supported'); return; }
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    const r = new SR(); r.continuous = false; r.interimResults = false; r.lang = 'en-US';
    r.onstart = () => setIsListening(true);
    r.onresult = (e) => setQuestion(p => p + (p ? ' ' : '') + e.results[0][0].transcript);
    r.onerror = () => setIsListening(false); r.onend = () => setIsListening(false);
    r.start();
  };

  const handleDrawCards = async () => {
    if (!canDraw) return;
    setLoading(true); setError('');
    setPhase('shuffling');
    try {
      const { data } = await api.post('/tarot/reading', {
        question: question || `Seeking guidance on: ${intentions.join(', ')}`,
        intention: intentions.join(', ') || 'clarity',
        spread_type: spreadType || 'the_mirror'
      });
      setCards(data.cards);
      setReadingData(data);
      setFlippedCards([]);
      if (data.surprise_message) setSurpriseMsg(data.surprise_message);
      if (data.level_up) setLevelUp(data.level_up);
      setPhase('drawing');
      // Slow reveal: flip cards one by one
      for (let i = 0; i < 5; i++) {
        await new Promise(r => setTimeout(r, 1200));
        setFlippedCards(prev => [...prev, i]);
      }
      setTimeout(() => setPhase('reading'), 1500);
    } catch (e) {
      if (e.response?.status === 402) navigate('/paywall');
      else setError(e.response?.data?.detail || 'Something went wrong. Please try again.');
      setPhase('question');
    } finally {
      setLoading(false);
    }
  };

  const positions = readingData?.positions || ['Situation', 'Challenge', 'Subconscious', 'Advice', 'Outcome'];

  return (
    <div className="min-h-screen bg-[#070A1A]" data-testid="home-page">
      <Navigation />
      <div className="px-4 md:px-12 lg:px-24 py-6 pb-24 md:pb-8 max-w-5xl mx-auto">
        <AnimatePresence mode="wait">
          {/* QUESTION PHASE */}
          {phase === 'question' && (
            <motion.div key="question" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
              <div className="text-center mb-6">
                <h1 className="font-['Playfair_Display'] text-2xl md:text-3xl text-[#FFFFF0]" data-testid="reading-greeting">
                  Hello, {user?.username || 'dear one'}. How are you feeling today?
                </h1>
                <p className="text-sm text-[#D4AF37] mt-3" data-testid="intention-label">
                  Select a Healing Intention (Optional)
                </p>
              </div>

              {/* Healing Intentions with symbols — multi-select */}
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mb-8" data-testid="intention-tabs">
                {INTENTIONS.map(intent => {
                  const sel = intentions.includes(intent.id);
                  return (
                    <button key={intent.id} onClick={() => setIntentions(prev => sel ? prev.filter(i => i !== intent.id) : [...prev, intent.id])} data-testid={`intention-${intent.id}`}
                      className={`glass-card px-3 py-3 text-left transition-all duration-300 ${sel ? 'border-[#D4AF37]/50 bg-[#D4AF37]/5 shadow-[0_0_20px_rgba(212,175,55,0.1)]' : 'hover:border-white/20'}`}>
                      <p className={`text-xs ${sel ? 'text-[#FFFFF0]' : 'text-[#BDB2C4]'}`}>
                        <span className="text-[#D4AF37] mr-1.5">{intent.symbol}</span>
                        {intent.label}
                      </p>
                      {sel && <p className="text-[10px] text-[#D4AF37]/70 mt-1 italic">{intent.affirmation}</p>}
                    </button>
                  );
                })}
              </div>

              {/* Question heading */}
              <h2 className="font-['Playfair_Display'] text-2xl md:text-3xl text-[#FFFFF0] text-center mb-2" data-testid="question-heading">
                What's on your mind?
              </h2>
              <p className="text-sm text-[#D4AF37] text-center mb-6" data-testid="question-subtitle">
                Ask your question, seek guidance, or simply express how you are feeling.
              </p>

              {/* Question Input */}
              <div className="glass-card p-6 mb-6" data-testid="question-input-area">
                <div className="relative">
                  <textarea data-testid="question-textarea" value={summarizing ? '' : question} onChange={e => setQuestion(e.target.value)}
                    placeholder={summarizing ? "The Guide is framing your question..." : "Type your question here...."}
                    disabled={summarizing}
                    className="w-full h-28 bg-transparent text-[#FFFFF0] placeholder:text-[#BDB2C4]/40 resize-none focus:outline-none text-base leading-relaxed disabled:opacity-60" />
                  <button onClick={startListening} data-testid="voice-input-btn"
                    className={`absolute bottom-2 right-2 p-2 rounded-full transition-all ${isListening ? 'bg-[#C87886]/30 text-[#C87886] animate-pulse' : 'bg-white/5 text-[#BDB2C4] hover:text-[#D4AF37]'}`}>
                    <Mic size={18} />
                  </button>
                </div>
              </div>

              {error && <p className="text-sm text-[#C87886] text-center mb-4" data-testid="reading-error">{error}</p>}

              {/* Spread Selector Dropdown */}
              {spreads.length > 1 && (
                <div className="mb-6 flex justify-center" data-testid="spread-selector">
                  <div className="relative inline-block">
                    <select
                      value={spreadType}
                      onChange={e => setSpreadType(e.target.value)}
                      data-testid="spread-dropdown"
                      className="glass-card appearance-none px-5 py-3 pr-10 text-sm text-[#FFFFF0] bg-white/5 border-white/10 focus:border-[#D4AF37] rounded-xl cursor-pointer transition-all hover:border-[#D4AF37]/40"
                    >
                      <option value="" disabled className="bg-[#0F0B1E] text-[#BDB2C4]">Choose Your Spread</option>
                      {spreads.map(s => (
                        <option key={s.id} value={s.id} disabled={s.locked} className="bg-[#0F0B1E] text-[#FFFFF0]">
                          {s.locked ? `\uD83D\uDD12 ${s.name} (Lvl ${s.unlock_level})` : s.name}
                        </option>
                      ))}
                    </select>
                    <ChevronDown size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#D4AF37] pointer-events-none" />
                  </div>
                </div>
              )}

              <div className="flex flex-col items-center gap-3">
                <button onClick={handleDrawCards} disabled={loading || !canDraw}
                  className="btn-primary inline-flex items-center gap-3 px-10 py-4 text-base disabled:opacity-30 disabled:cursor-not-allowed"
                  data-testid="draw-cards-btn">
                  {loading ? <><Loader2 size={18} className="animate-spin" /> The cards are listening...</>
                    : <><Sparkles size={18} /> Draw My Cards <ArrowRight size={18} /></>}
                </button>
              </div>
            </motion.div>
          )}

          {/* SHUFFLING PHASE - Visual card shuffle animation */}
          {phase === 'shuffling' && (
            <motion.div key="shuffling" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="flex flex-col items-center justify-center py-16">
              {/* Orbiting glow ring */}
              <div className="relative w-48 h-64 mb-10" data-testid="shuffle-animation">
                <motion.div
                  className="absolute -inset-4 rounded-2xl"
                  animate={{ opacity: [0.05, 0.15, 0.05] }}
                  transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
                  style={{ background: 'radial-gradient(circle, rgba(212,175,55,0.15) 0%, transparent 70%)' }}
                />
                {[0, 1, 2, 3, 4].map(i => (
                  <motion.div key={i}
                    className="absolute inset-0 rounded-md border-2 border-[#D4AF37]/40 bg-gradient-to-br from-[#1D1129] via-[#0F0B1E] to-[#1D1129]"
                    animate={{
                      x: [0, (i - 2) * 30, 0, -(i - 2) * 25, 0],
                      y: [0, -10, 5, -5, 0],
                      rotate: [0, (i - 2) * 8, -(i - 2) * 5, (i - 2) * 3, 0],
                    }}
                    transition={{ duration: 2, repeat: Infinity, ease: "easeInOut", delay: i * 0.15 }}
                  >
                    <div className="absolute inset-2 rounded-sm border border-[#D4AF37]/20 flex items-center justify-center">
                      <div className="w-10 h-10 rounded-full border border-[#D4AF37]/40 flex items-center justify-center">
                        <motion.div
                          className="w-5 h-5 rounded-full bg-[#D4AF37]/20"
                          animate={{ scale: [1, 1.3, 1], opacity: [0.3, 0.7, 0.3] }}
                          transition={{ duration: 1.5, repeat: Infinity, delay: i * 0.2 }}
                        />
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>

              {/* Rotating messages */}
              <ShuffleMessages />
            </motion.div>
          )}

          {/* DRAWING PHASE - Cards revealed one by one */}
          {phase === 'drawing' && (
            <motion.div key="drawing" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
              <div className="text-center mb-8">
                <h2 className="font-['Playfair_Display'] text-2xl text-[#FFFFF0]">Your cards have been drawn</h2>
                <p className="text-sm text-[#D4AF37] mt-2 font-['Playfair_Display'] italic">Watch as they reveal themselves to you...</p>
              </div>
              <div className="flex flex-wrap justify-center gap-4 md:gap-5" data-testid="card-draw-area">
                {cards.map((card, i) => (
                  <div key={i} className="flex flex-col items-center gap-2">
                    <p className="text-[10px] text-[#D4AF37] tracking-widest uppercase">{positions[i]}</p>
                    <FlippableCard card={card} position={positions[i]} index={i} isFlipped={flippedCards.includes(i)} onFlip={() => {}} />
                    {flippedCards.includes(i) && (
                      <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-xs text-[#FFFFF0] text-center max-w-[120px] font-['Playfair_Display']">
                        {card.name}
                      </motion.p>
                    )}
                  </div>
                ))}
              </div>
            </motion.div>
          )}

          {/* READING PHASE */}
          {phase === 'reading' && readingData && (
            <motion.div key="reading" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} ref={readingRef}>
              <div className="text-center mb-8">
                <h2 className="font-['Playfair_Display'] text-2xl md:text-3xl text-[#FFFFF0]">Your Reading</h2>
                <p className="text-sm text-[#D4AF37] mt-1">{readingData.spread_name}</p>
              </div>

              {/* All 5 cards displayed with names */}
              <div className="flex flex-wrap justify-center gap-4 md:gap-5 mb-10" data-testid="all-cards-display">
                {cards.map((card, i) => (
                  <div key={i} className="flex flex-col items-center gap-2">
                    <TarotCardFront key={i} card={card} position={positions[i]} index={i} size="medium" />
                    <p className="text-xs text-[#FFFFF0] text-center max-w-[130px] font-['Playfair_Display']">
                      {card.name}
                      {card.is_reversed && <span className="text-[#C87886] ml-1">(R)</span>}
                    </p>
                  </div>
                ))}
              </div>

              {/* All interpretations */}
              <div className="space-y-6 mb-8" data-testid="all-interpretations">
                {readingData.reading?.card_interpretations?.map((interp, i) => (
                  <motion.div key={i} initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.15 }}
                    className="glass-card p-6" data-testid={`interpretation-${i}`}>
                    <div className="flex items-center gap-3 mb-3">
                      {cards[i]?.image_url && <img src={cards[i].image_url} alt={cards[i]?.name} className="w-14 h-22 rounded-md object-cover border border-[#D4AF37]/30" />}
                      <div>
                        <h3 className="font-['Playfair_Display'] text-lg text-[#FFFFF0]">
                          {interp.card_name || cards[i]?.name}
                          {cards[i]?.is_reversed && <span className="text-sm text-[#C87886] ml-2 italic">Reversed</span>}
                        </h3>
                        <p className="text-xs text-[#D4AF37] uppercase tracking-widest">{interp.position || positions[i]}</p>
                      </div>
                    </div>
                    <p className="text-sm text-[#BDB2C4] leading-relaxed">{interp.interpretation}</p>
                    {interp.reflection_prompt && (
                      <p className="text-xs text-[#C87886] mt-3 italic border-t border-white/5 pt-3">{interp.reflection_prompt}</p>
                    )}
                  </motion.div>
                ))}
              </div>

              {/* Unified Narrative */}
              <div className="glass-card p-6 md:p-8 mb-6" data-testid="unified-narrative">
                <h3 className="font-['Playfair_Display'] text-xl text-[#FFFFF0] mb-4">The Full Story</h3>
                <p className="text-sm text-[#BDB2C4] leading-relaxed">{readingData.reading?.unified_narrative}</p>
              </div>

              {/* Affirmation */}
              <div className="glass-card p-6 md:p-8 mb-6 border-[#D4AF37]/20" style={{ background: 'rgba(212,175,55,0.03)' }} data-testid="affirmation-box">
                <p className="text-xs text-[#D4AF37] tracking-widest uppercase mb-3">Your Affirmation</p>
                <p className="text-base text-[#FFFFF0] font-['Playfair_Display'] italic">{readingData.reading?.affirmation}</p>
              </div>

              {/* Reflection Exercise */}
              <div className="glass-card p-6 md:p-8 mb-6 border-[#C87886]/20" style={{ background: 'rgba(200,120,134,0.03)' }} data-testid="reflection-box">
                <p className="text-xs text-[#C87886] tracking-widest uppercase mb-3">Your Reflection</p>
                <p className="text-sm text-[#BDB2C4] leading-relaxed italic">{readingData.reading?.reflection_exercise}</p>
                <p className="text-sm text-[#FFFFF0] mt-4 font-['Playfair_Display']">
                  Tonight, ask yourself: {readingData.reading?.journal_prompt}
                </p>
              </div>

              {surpriseMsg && (
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.5 }}
                  className="glass-card p-4 mb-6 border-[#D4AF37]/30 text-center" data-testid="surprise-message">
                  <p className="text-sm text-[#D4AF37] font-['Playfair_Display'] italic">{surpriseMsg}</p>
                </motion.div>
              )}

              {/* Level Up Animation */}
              {levelUp && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.3, type: "spring", stiffness: 200 }}
                  className="glass-card p-8 mb-6 border-[#D4AF37]/40 text-center" style={{ background: 'rgba(212,175,55,0.05)' }}
                  data-testid="level-up-animation"
                >
                  <motion.div
                    animate={{ rotate: [0, 360] }}
                    transition={{ duration: 3, ease: "linear", repeat: 2 }}
                    className="w-20 h-20 rounded-full bg-[#D4AF37]/10 border-2 border-[#D4AF37]/50 flex items-center justify-center mx-auto mb-4"
                  >
                    <span className="text-3xl text-[#D4AF37]">{['\u2609', '\u2609', '\u263F', '\u2640', '\u2643', '\u2644', '\u2645', '\u2646'][levelUp.new_level] || '\u2728'}</span>
                  </motion.div>
                  <p className="text-xs text-[#D4AF37] tracking-widest uppercase mb-1">Level Up!</p>
                  <h3 className="font-['Playfair_Display'] text-2xl text-[#FFFFF0] mb-1">Level {levelUp.new_level}: {levelUp.name}</h3>
                  <p className="text-sm text-[#BDB2C4] italic">{levelUp.description}</p>
                </motion.div>
              )}

              {/* Share Reading */}
              <div className="mt-8 mb-4">
                <ShareReading cards={cards} readingData={readingData} type="reading" />
              </div>

              <div className="flex justify-center gap-4 mt-4">
                <button onClick={() => { setPhase('question'); setQuestion(''); setIntentions([]); setCards([]); setReadingData(null); setFlippedCards([]); setSpreadType(''); }}
                  className="btn-primary" data-testid="new-reading-btn">New Reading</button>
                <button onClick={() => navigate('/journal')} className="text-sm text-[#BDB2C4] hover:text-[#D4AF37] transition-colors underline underline-offset-4" data-testid="view-journal-btn">
                  View in Journal</button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
